<script setup lang="ts">
/**
 * 付款二维码面板。
 *
 * 三个必须做对的细节：
 *   1. 二维码要够大（顾客站在半米外扫），所以 SVG 铺满整个面板；
 *   2. 有倒计时：二维码 5 分钟就过期，过期不说清楚，顾客会一直扫一张废码；
 *   3. 金额就写在二维码正下方 —— 顾客付款前最后一次核对金额的机会。
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { formatYuan } from '@jinhua/shared';
import { renderQrSvg } from '../utils/qr';
import { remainingSeconds } from '../payments/pay-polling';

const props = defineProps<{
  payload: string;
  amountCents: number;
  expiresAt: string;
  /** 模拟收款时二维码上必须挂标记，防止顾客以为真的付过钱了 */
  isSimulated: boolean;
}>();

const emit = defineEmits<{ (e: 'expire'): void }>();

const svg = ref('');
const error = ref<string | null>(null);
const now = ref(Date.now());
let timer: ReturnType<typeof setInterval> | null = null;

const expiresAtMs = computed(() => new Date(props.expiresAt).getTime());
const left = computed(() => remainingSeconds(expiresAtMs.value, now.value));
const amountText = computed(() => formatYuan(props.amountCents));

function build(): void {
  error.value = null;
  try {
    svg.value = renderQrSvg(props.payload, 8, 8);
  } catch (e) {
    svg.value = '';
    error.value = (e as Error).message;
  }
}

watch(() => props.payload, build, { immediate: true });

onMounted(() => {
  // 每秒走一格：倒计时是给顾客看的，频率不用更高
  timer = setInterval(() => {
    now.value = Date.now();
    if (now.value >= expiresAtMs.value) emit('expire');
  }, 1000);
});

onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});
</script>

<template>
  <div class="qr">
    <div v-if="error" class="qr__error">
      <div class="qr__error-icon">⚠️</div>
      <div>{{ error }}</div>
      <div class="muted">请叫服务员处理，不要离开</div>
    </div>

    <div v-else class="qr__box">
      <!-- SVG 由本地二维码组件生成（不联网、不依赖 CDN） -->
      <div class="qr__svg" v-html="svg" />
      <div v-if="isSimulated" class="qr__stamp">模拟支付<br />不会真收钱</div>
    </div>

    <div class="qr__amount">
      <span class="muted">应付</span>
      <span class="amount amount--big">¥{{ amountText }}</span>
    </div>

    <div class="qr__hint">
      <span>请用<b>微信</b>扫一扫上面的二维码</span>
      <span class="qr__countdown" :class="{ 'qr__countdown--low': left <= 30 }">
        二维码剩余 {{ left }} 秒
      </span>
    </div>
  </div>
</template>

<style scoped>
.qr {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.qr__box {
  position: relative;
  background: #fff;
  border-radius: 20px;
  padding: 12px;
  /* 尺寸跟着屏幕高度走：1280×800 这种常见触屏机上，
     必须保证「二维码 + 金额 + 状态」都在第一屏内，顾客不用滑动 */
  width: min(38vh, 330px);
  aspect-ratio: 1 / 1;
}

.qr__svg :deep(svg) {
  width: 100%;
  height: 100%;
  display: block;
}

/* 模拟支付的印章：斜着压在码上，任何角度都看得见 */
.qr__stamp {
  position: absolute;
  right: -14px;
  bottom: -14px;
  padding: 8px 14px;
  border-radius: 12px;
  background: rgba(217, 43, 43, 0.94);
  color: #fff;
  font-weight: 800;
  font-size: 16px;
  line-height: 1.2;
  text-align: center;
  border: 3px solid #fff;
}

.qr__amount {
  display: flex;
  align-items: baseline;
  gap: 12px;
}

.qr__hint {
  display: flex;
  align-items: center;
  gap: 18px;
  font-size: 19px;
  color: var(--muted);
}

.qr__countdown--low {
  color: var(--warn);
  font-weight: 700;
}

.qr__error {
  width: min(38vh, 330px);
  aspect-ratio: 1 / 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: var(--panel-2);
  border: 2px dashed var(--line);
  border-radius: 20px;
  text-align: center;
  padding: 24px;
}

.qr__error-icon {
  font-size: 48px;
}

@media (max-width: 860px) {
  .qr {
    gap: 10px;
  }

  /* 手机上要同时满足「不超出屏幕宽」和「别把取餐号挤出屏幕」 */
  .qr__box,
  .qr__error {
    width: min(42vh, 76vw, 330px);
  }

  .qr__hint {
    flex-direction: column;
    gap: 4px;
    font-size: 14px;
    text-align: center;
  }

  .qr__stamp {
    right: -8px;
    bottom: -8px;
    font-size: 13px;
    padding: 6px 10px;
  }
}

</style>
